import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import nodeValidationService from '../../../services/nodeValidationService';

const WorkflowToolbar = ({
  currentWorkflow,
  workflows,
  onWorkflowChange,
  onSave,
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onExport,
  canUndo,
  canRedo,
  isSaving,
  isSelectionMode,
  onToggleSelectionMode,
  onSelectAll,
  onClearSelection,
  onGroupDelete,
  selectedCount,
  nodes,
  connections,
  lastAutosaveTime,
  autosaveStatus,
  onVersionHistory,
  hasUnsavedChanges = false
}) => {
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const handleValidateWorkflow = () => {
    const result = nodeValidationService?.validateWorkflow(nodes || [], connections || []);
    setValidationResult(result);
    setShowValidationModal(true);
  };

  const closeValidationModal = () => {
    setShowValidationModal(false);
  };

  const workflowOptions = workflows?.map(wf => ({
    value: wf?.id,
    label: wf?.name,
    description: `${wf?.nodeCount} nodes • Modified ${wf?.lastModified}`
  }));

  const exportOptions = [
    { value: 'json', label: 'Export as JSON' },
    { value: 'pdf', label: 'Export as PDF' },
    { value: 'png', label: 'Export as Image' },
    { value: 'schedule', label: 'Export to Schedule' }
  ];

  const handleExport = (format) => {
    if (onExport) {
      onExport(format);
    }
  };

  const formatLastSaveTime = () => {
    if (!lastAutosaveTime) return 'Not saved yet';
    
    try {
      const saveDate = new Date(lastAutosaveTime);
      const now = new Date();
      const diffMs = now - saveDate;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      
      if (diffSecs < 60) {
        return 'Just now';
      } else if (diffMins < 60) {
        return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
      } else {
        return saveDate?.toLocaleTimeString();
      }
    } catch (error) {
      return 'Unknown';
    }
  };

  const getAutosaveStatusIcon = () => {
    switch (autosaveStatus) {
      case 'saving':
        return { name: 'Loader', color: '#3b82f6', spin: true };
      case 'saved':
        return { name: 'CheckCircle', color: '#10b981', spin: false };
      case 'error':
        return { name: 'AlertCircle', color: '#ef4444', spin: false };
      default:
        return { name: 'Cloud', color: '#6b7280', spin: false };
    }
  };

  const autosaveIcon = getAutosaveStatusIcon();

  return (
    <div className="bg-card border-b border-border px-4 py-3 flex items-center justify-between gap-4">
      {/* Left Section - Workflow Selector */}
      <div className="flex items-center gap-3">
        <div className="w-64">
          <Select
            value={currentWorkflow}
            onChange={onWorkflowChange}
            options={workflowOptions}
            placeholder="Select workflow"
          />
        </div>
      </div>

      {/* Center Section - Actions */}
      <div className="flex items-center gap-2">
        {/* Save Button - Now opens modal */}
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          disabled={isSaving}
          className="gap-2"
        >
          <Icon name={isSaving ? 'Loader' : 'Save'} size={16} className={isSaving ? 'animate-spin' : ''} />
          {isSaving ? 'Saving...' : 'Save Workflow'}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          iconName="Undo"
          onClick={onUndo}
          disabled={!canUndo}
          aria-label="Undo"
        />
        <Button
          variant="ghost"
          size="sm"
          iconName="Redo"
          onClick={onRedo}
          disabled={!canRedo}
          aria-label="Redo"
        />

        <div className="w-px h-8 bg-border hidden md:block" />

        <Button
          variant="ghost"
          size="sm"
          iconName="Play"
          onClick={() => console.log('Simulate workflow')}
        >
          <span className="hidden md:inline ml-2">Simulate</span>
        </Button>

        <Button
          variant="ghost"
          size="sm"
          iconName="Download"
          onClick={() => handleExport('json')}
        >
          <span className="hidden md:inline ml-2">Export</span>
        </Button>

        <Button
          variant="primary"
          size="sm"
          iconName="Save"
          onClick={onSave}
          loading={isSaving}
        >
          <span className="hidden md:inline ml-2">Save</span>
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onVersionHistory}
          className="flex items-center gap-2"
        >
          <Icon name="GitBranch" className="w-4 h-4" />
          <span className="hidden lg:inline">Versions</span>
        </Button>

        <Button
          onClick={handleValidateWorkflow}
          variant="outline"
          className="border-blue-600 text-blue-600 hover:bg-blue-50"
        >
          <Icon name="CheckCircle" size={16} className="mr-2" />
          Validate Workflow
        </Button>
      </div>

      {/* Right Section - Status and Version History */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-success" />
          <span className="caption text-muted-foreground">ERP Connected</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-success" />
          <span className="caption text-muted-foreground">MES Synced</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-warning" />
          <span className="caption text-muted-foreground">Equipment Status</span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <div className={autosaveIcon?.spin ? 'animate-spin' : ''}>
            <Icon name={autosaveIcon?.name} size={14} color={autosaveIcon?.color} />
          </div>
          <span className="caption text-muted-foreground">
            {autosaveStatus === 'saving' ? 'Saving...' : 
             autosaveStatus === 'saved' ? 'Saved' :
             autosaveStatus === 'error' ? 'Save failed' :
             `Last saved: ${formatLastSaveTime()}`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default WorkflowToolbar;